import { type FC, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BsCaretDownFill, BsThreeDotsVertical, BsExclamationTriangle } from 'react-icons/bs';

import { type RootState } from '@editor2/reducers/root';
import { type PageElement, ElementType } from '@editor2/types/pageElements';
import { isModified, isNew } from '@editor2/services/changesStatusService';
import {
	isAllowedInNesting,
	isAllowedInQuestionDeck,
	isDeprecated
} from '@editor2/services/elementService';
import { useEventListener } from '@editor2/hooks/useEventListener';
import ButtonTooltip from '@editor2/components/ButtonTooltip';
import {
	NewBadge,
	ModifiedBadge,
	WarningBadge,
	GeneratingQuestionsBadge,
	FeedbackNeededBadge
} from '@editor2/components/Badge';
import { elementOptions } from '@editor2/containers/PageElementsPage/styles';
import ElementDropdown from './ElementDropdown';
import { findNearestDoubleElement } from '@editor2/services/doubleElementValidationsService';
import IconButton from '@editor2/components/IconButton';

interface Props {
	courseId: number;
	pageId: string;
	element: PageElement;
	index: number;
	collapseElements: boolean;
	onCollapse: () => void;
}

const ElementOptionsBlock: FC<Props> = (props) => {
	const { courseId, pageId, element, index, collapseElements, onCollapse } = props;

	const { changes, pages, elements } = useSelector((state: RootState) => state.courses[courseId]);

	const [isDropdownShown, setDropdownShown] = useState(false);
	const ref = useRef(null);

	const handleClickOutside = (event) => {
		if (ref.current && !ref.current.contains(event.target)) setDropdownShown(false);
	};

	useEventListener('click', handleClickOutside);

	const hideCollapseIcon = () => {
		return (
			(element.type === ElementType.QuestionDeck ||
				element.type === ElementType.NestingInstruction) &&
			element.direction === 'out'
		);
	};

	const deprecatedMessage =
		'This is a deprecated element. Contact the tech department for assistance upgrading this content.';

	const notAllowedInNestingMessage =
		'This element is not allowed in the Nesting Container. Please move it out.';

	const notAllowedInQuestionDeckMessage =
		'This element is not allowed in the Question Deck. Please move it out.';

	const checkAllowedInsideNestingInstruction = () => {
		const pageElements = pages.entities[pageId].elements;
		const nearestDoubleElement = findNearestDoubleElement(pageElements, index, elements);
		return (
			nearestDoubleElement.type === ElementType.NestingInstruction &&
			!isAllowedInNesting(element.type)
		);
	};

	const checkAllowedInsideQuestionDeck = () => {
		const pageElements = pages.entities[pageId].elements;
		const nearestDoubleElement = findNearestDoubleElement(pageElements, index, elements);

		return (
			nearestDoubleElement.type === ElementType.QuestionDeck &&
			!isAllowedInQuestionDeck(element.type)
		);
	};

	const isGeneratingQuestions =
		element.type === ElementType.MCQuestionPool && element.isGeneratingQuestions;

	const isQuestionFeedbackNeeded =
		element.type === ElementType.MCQuestionPool && element.needsQuestionFeedback;

	return (
		<div className={elementOptions}>
			{isNew(changes.elements[element.family_id]) && <NewBadge />}

			{isModified(changes.elements[element.family_id]) && <ModifiedBadge />}

			{isGeneratingQuestions && <GeneratingQuestionsBadge />}

			{isQuestionFeedbackNeeded && <FeedbackNeededBadge />}

			{element.indent && checkAllowedInsideNestingInstruction() && (
				<ButtonTooltip message={notAllowedInNestingMessage}>
					<span>
						<WarningBadge />
					</span>
				</ButtonTooltip>
			)}

			{element.indent && checkAllowedInsideQuestionDeck() && (
				<ButtonTooltip message={notAllowedInQuestionDeckMessage}>
					<span>
						<WarningBadge />
					</span>
				</ButtonTooltip>
			)}

			{isDeprecated(element.type) && (
				<ButtonTooltip message={deprecatedMessage}>
					<BsExclamationTriangle />
				</ButtonTooltip>
			)}

			{!isDeprecated(element.type) && (
				<>
					<IconButton onClick={() => onCollapse()} disabled={isGeneratingQuestions}>
						<BsCaretDownFill
							className={`${collapseElements ? 'collapseIconRotate' : 'collapseIcon'} ${
								hideCollapseIcon() && 'collapseIconHidden'
							}`}
						/>
					</IconButton>

					<span ref={ref} className="optionsIcon">
						<IconButton
							onClick={() => setDropdownShown(!isDropdownShown)}
							disabled={isGeneratingQuestions}>
							<BsThreeDotsVertical />
						</IconButton>

						{isDropdownShown && (
							<ElementDropdown
								element={element}
								pageId={pageId}
								index={index}
								courseId={courseId}
								onDropdownHide={() => setDropdownShown(false)}
							/>
						)}
					</span>
				</>
			)}
		</div>
	);
};

export default ElementOptionsBlock;
